import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { doCreateIssue } from '../../../modules/issues/stores/thunk';
import { ROLES } from '../../../constants';

import { Button } from '../../../components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Bike } from 'lucide-react';
import { ShopOutlined } from '@ant-design/icons';
import { ISSUE_REASONS, IssueReason } from './issue.types';

interface NewReportIssueFormProps {
  issue: {
    orderId: string;
    carrierName?: string;
    companyName?: string;
  };
  userId: string;
  onCancel: () => void;
  onSuccess?: (_payload: any) => void;
}

const NewReportIssueForm: React.FC<NewReportIssueFormProps> = ({ issue, userId, onCancel, onSuccess }) => {
  const { orderId, carrierName, companyName } = issue;
  const dispatch = useDispatch<AppDispatch>();
  const [currentLength, setCurrentLength] = useState(0);
  const userRole = useSelector((state: RootState) => state.auth.user.role);

  const formSchema = z.object({
    sharedWith:
      userRole === ROLES.ADMIN
        ? z.enum(['CARRIER', 'COMPANY'], { message: 'Please select the carrier or merchant' })
        : z.optional(z.enum(['CARRIER', 'COMPANY'], { message: 'Please select the carrier or merchant' })),
    reason: z.enum(ISSUE_REASONS.map((r) => r.value) as [IssueReason, ...IssueReason[]], {
      message: 'Please select a reason',
    }),
    message: z.string().min(1, { message: 'Please describe the issue' }).max(500, {
      message: 'Message cannot exceed 500 characters',
    }),
  });
  type FormValues = z.infer<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: undefined,
      message: '',
    },
  });

  const handleMessageChange = (value: string) => {
    setCurrentLength(value.length);
  };

  const onSubmit = async (values: FormValues) => {
    const { payload } = await dispatch(
      doCreateIssue({
        orderId,
        creatorUserId: userId,
        ...values,
      }),
    );

    form.reset();

    if (onSuccess) {
      onSuccess(payload);
    }

    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {userRole === ROLES.ADMIN && (
          <FormField
            control={form.control}
            name="sharedWith"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Share with</FormLabel>
                <FormControl>
                  <RadioGroup
                    {...field}
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-row space-x-2"
                  >
                    <div
                      className={`flex-1 flex items-center space-x-2 border border-input p-3 rounded-md ${field.value === 'CARRIER' ? 'border-primary' : ''}`}
                    >
                      <RadioGroupItem value="CARRIER" id="carrier" className="sr-only" />
                      <Bike className="h-4 w-4 mr-1" />
                      <label htmlFor="carrier" className="flex-1 cursor-pointer">
                        {carrierName}
                      </label>
                    </div>
                    <div
                      className={`flex-1 flex items-center space-x-2 border border-input p-3 rounded-md ${field.value === 'COMPANY' ? 'border-primary' : ''}`}
                    >
                      <RadioGroupItem value="COMPANY" id="company" className="sr-only" />
                      <ShopOutlined className="h-4 w-4 mr-1" />
                      <label htmlFor="company" className="flex-1 cursor-pointer">
                        {companyName}
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {ISSUE_REASONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Please describe the issue"
                  className="resize-none"
                  rows={6}
                  maxLength={500}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handleMessageChange(e.target.value);
                  }}
                />
              </FormControl>
              <div className="text-right text-sm text-muted-foreground">{currentLength}/500</div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end space-x-4 mt-6">
          <Button variant="outline" onClick={onCancel} type="button">
            Cancel
          </Button>

          <Button type="submit" disabled={!form.formState.isValid}>
            Submit Issue
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default NewReportIssueForm;
